import React, { useCallback, useEffect, useState } from 'react';
import { api, ApiError, downloadFile, mutate } from '../shared/api';
import { formatDate, formatMoney, formatNumber } from '../shared/formatting';

type Locale = 'hr' | 'en';

type Stage = 'bez_avansa' | 'avans_placen' | 'finalna_izdana' | 'razlika_naplacena' | 'zatvoreno';

type Offer = {
    id: number;
    offer_no: string;
    offer_year: number;
    offer_date: string | null;
    partner_name: string;
    partner_sifra: string | null;
    offer_eur: number | null;
    advance_eur: number | null;
    advance_paid_at: string | null;
    advance_bank_entry_id: number | null;
    ad_invoice_no: string | null;
    outgoing_invoice_id: number | null;
    final_invoice_eur: number | null;
    processing_eur: number | null;
    processing_invoice_no: string | null;
    ura_invoice_id: number | null;
    difference_eur: number | null;
    difference_paid_at: string | null;
    difference_bank_entry_id: number | null;
    fine_au_offer_g: number | null;
    fine_ag_offer_g: number | null;
    fine_au_analysis_g: number | null;
    fine_ag_bars_g: number | null;
    note: string | null;
    source: string;
    dug_eur: number;
    stage: Stage;
    fine_dev_pct: number | null;
};

type Totals = {
    count: number;
    offer_eur: number;
    advance_eur: number;
    final_invoice_eur: number;
    processing_eur: number;
    difference_eur: number;
    dug_eur: number;
};

type ListResp = { rows: Offer[]; totals: Totals; stages: Record<Stage, string>; years: number[] };

type BankRow = { id: number; statement_date: string; statement_number?: number; partner_name: string | null; description: string | null; credit: number | string; debit: number | string };

type Detail = {
    offer: Offer;
    outgoing_invoice: { id: number; invoice_number: string; document_date: string; customer_name: string | null; amount_eur: number | string } | null;
    ura_invoice: { id: number; invoice_number: string | null; document_number: string | null; document_date: string | null; supplier_name: string | null; amount_total: number | string } | null;
    bank: { advance?: BankRow; difference?: BankRow };
    timeline: { date: string | null; step: string; amount_eur: number | null; ref: string | null }[];
    events: { id: number; action: string; field: string | null; old_value: string | null; new_value: string | null; created_at: string; user_name: string | null }[];
};

type Filters = {
    year: string;
    from: string;
    to: string;
    stage: string;
    has_processing: string;
    missing_final: boolean;
    unpaid_difference: boolean;
    fine_dev_over: string;
    q: string;
};

const EMPTY_FILTERS: Filters = { year: '', from: '', to: '', stage: '', has_processing: '', missing_final: false, unpaid_difference: false, fine_dev_over: '', q: '' };

const FORM_FIELDS: { key: keyof Offer; hr: string; en: string; kind: 'text' | 'date' | 'num' }[] = [
    { key: 'offer_no', hr: 'Broj ponude', en: 'Offer no.', kind: 'text' },
    { key: 'offer_date', hr: 'Datum ponude', en: 'Offer date', kind: 'date' },
    { key: 'partner_name', hr: 'Kupac', en: 'Customer', kind: 'text' },
    { key: 'offer_eur', hr: 'Iznos ponude EUR', en: 'Offer EUR', kind: 'num' },
    { key: 'advance_eur', hr: 'Avans EUR', en: 'Advance EUR', kind: 'num' },
    { key: 'advance_paid_at', hr: 'Datum avansa', en: 'Advance date', kind: 'date' },
    { key: 'ad_invoice_no', hr: 'Faktura AD', en: 'AD invoice', kind: 'text' },
    { key: 'final_invoice_eur', hr: 'Finalna faktura EUR', en: 'Final invoice EUR', kind: 'num' },
    { key: 'processing_eur', hr: 'Troškovi obrade EUR', en: 'Processing EUR', kind: 'num' },
    { key: 'processing_invoice_no', hr: 'Račun za obradu', en: 'Processing invoice', kind: 'text' },
    { key: 'difference_eur', hr: 'Razlika nakon analize EUR', en: 'Post-analysis payment EUR', kind: 'num' },
    { key: 'difference_paid_at', hr: 'Datum razlike', en: 'Payment date', kind: 'date' },
    { key: 'fine_au_offer_g', hr: 'Fine Au po ponudi g', en: 'Fine Au offer g', kind: 'num' },
    { key: 'fine_au_analysis_g', hr: 'Fine Au po analizi g', en: 'Fine Au analysis g', kind: 'num' },
    { key: 'fine_ag_offer_g', hr: 'Fine Ag po ponudi g', en: 'Fine Ag offer g', kind: 'num' },
    { key: 'fine_ag_bars_g', hr: 'Fine Ag iz poluga g', en: 'Fine Ag bars g', kind: 'num' },
];

function qs(f: Filters): string {
    const p = new URLSearchParams();
    if (f.year) p.set('year', f.year);
    if (f.from) p.set('from', f.from);
    if (f.to) p.set('to', f.to);
    if (f.stage) p.set('stage', f.stage);
    if (f.has_processing) p.set('has_processing', f.has_processing);
    if (f.missing_final) p.set('missing_final', '1');
    if (f.unpaid_difference) p.set('unpaid_difference', '1');
    if (f.fine_dev_over) p.set('fine_dev_over', f.fine_dev_over.replace(',', '.'));
    if (f.q.trim()) p.set('q', f.q.trim());
    const s = p.toString();
    return s ? `?${s}` : '';
}

const toNum = (v: string): number | null => {
    const s = v.trim().replace(/\s/g, '');
    if (!s) return null;
    const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
    return Number.isFinite(n) ? n : null;
};

/** Modul 40.46 Računi TCA: ponude TCA od avansa do finalne fakture, obrada, razlika nakon analize i dug TCA. */
export function TcaInvoicesModule({ locale, token, onBack }: { locale: Locale; token: string | null; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const money = (v: number | null | undefined) => (v === null || v === undefined ? '' : formatMoney(v, locale));

    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
    const [data, setData] = useState<ListResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openId, setOpenId] = useState<number | 'new' | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        api<ListResp>(`/tca-invoices${qs(filters)}`, token)
            .then(setData)
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, filters]);

    useEffect(() => {
        load();
    }, [load]);

    const exportXlsx = async () => {
        try {
            await downloadFile(`/tca-invoices/export.xlsx${qs(filters)}`, token, `racuni-tca-${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch {
            setError(t('Izvoz nije uspio.', 'Export failed.'));
        }
    };

    const set = <K extends keyof Filters>(k: K, v: Filters[K]) => setFilters((f) => ({ ...f, [k]: v }));
    const stageLabel = (s: Stage) => data?.stages[s] ?? s;

    const th: React.CSSProperties = { textAlign: 'left', padding: '0.35rem 0.5rem', borderBottom: '1px solid #999', whiteSpace: 'nowrap' };
    const td: React.CSSProperties = { padding: '0.3rem 0.5rem', borderBottom: '1px solid #ddd', verticalAlign: 'top' };
    const num: React.CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
    const thn: React.CSSProperties = { ...th, textAlign: 'right' };
    const tot = data?.totals;

    return (
        <div className="module-shell">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                    {onBack ? (
                        <button type="button" className="secondary compact" onClick={onBack}>
                            ← {t('Skladište', 'Warehouse')}
                        </button>
                    ) : null}
                    <p className="eyebrow">40.46</p>
                    <h2 style={{ margin: '0.2rem 0' }}>{t('Računi TCA', 'TCA invoices')}</h2>
                    <p className="muted" style={{ margin: 0 }}>
                        {t(
                            'Ponuda, avans, finalna faktura, račun TCA za obradu i razlika nakon analize. Dug je finalna faktura umanjena za avans, obradu i razliku, a svi zbrojevi računaju se nad istim filtriranim ponudama.',
                            'Offer, advance, final invoice, TCA processing invoice and post-analysis payment. Balance is the final invoice less advance, processing and payment; all totals use the same filtered offers.',
                        )}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button type="button" className="compact" onClick={() => setOpenId('new')}>
                        {t('Nova ponuda', 'New offer')}
                    </button>
                    <button type="button" className="secondary compact" onClick={load} disabled={loading}>
                        {t('Osvježi', 'Refresh')}
                    </button>
                    <button type="button" className="secondary compact" onClick={exportXlsx} disabled={!data}>
                        {t('Izvoz u Excel', 'Export to Excel')}
                    </button>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.8rem', alignItems: 'center', marginTop: '0.8rem' }}>
                <select value={filters.year} onChange={(e) => set('year', e.target.value)} aria-label={t('Godina', 'Year')}>
                    <option value="">{t('Sve godine', 'All years')}</option>
                    {(data?.years ?? []).map((y) => (
                        <option key={y} value={String(y)}>
                            {y}
                        </option>
                    ))}
                </select>
                <label>
                    {t('Od', 'From')} <input type="date" value={filters.from} onChange={(e) => set('from', e.target.value)} />
                </label>
                <label>
                    {t('Do', 'To')} <input type="date" value={filters.to} onChange={(e) => set('to', e.target.value)} />
                </label>
                <select value={filters.stage} onChange={(e) => set('stage', e.target.value)} aria-label={t('Faza', 'Stage')}>
                    <option value="">{t('Sve faze', 'All stages')}</option>
                    {data
                        ? (Object.keys(data.stages) as Stage[]).map((s) => (
                              <option key={s} value={s}>
                                  {data.stages[s]}
                              </option>
                          ))
                        : null}
                </select>
                <select value={filters.has_processing} onChange={(e) => set('has_processing', e.target.value)} aria-label={t('Račun za obradu', 'Processing invoice')}>
                    <option value="">{t('Obrada: sve', 'Processing: all')}</option>
                    <option value="1">{t('S računom za obradu', 'With processing invoice')}</option>
                    <option value="0">{t('Bez računa za obradu', 'Without processing invoice')}</option>
                </select>
                <label>
                    <input type="checkbox" checked={filters.missing_final} onChange={(e) => set('missing_final', e.target.checked)} /> {t('Nedostaje finalna faktura', 'Missing final invoice')}
                </label>
                <label>
                    <input type="checkbox" checked={filters.unpaid_difference} onChange={(e) => set('unpaid_difference', e.target.checked)} /> {t('Razlika nije naplaćena', 'Unpaid balance')}
                </label>
                <label>
                    {t('Fine odstupa više od %', 'Fine deviation over %')}{' '}
                    <input type="text" inputMode="decimal" style={{ width: '4rem' }} value={filters.fine_dev_over} onChange={(e) => set('fine_dev_over', e.target.value)} />
                </label>
                <input type="search" placeholder={t('Broj ponude, fakture...', 'Offer, invoice no...')} value={filters.q} onChange={(e) => set('q', e.target.value)} />
                <button type="button" className="secondary compact" onClick={() => setFilters(EMPTY_FILTERS)}>
                    {t('Očisti', 'Clear')}
                </button>
            </div>

            {tot ? (
                <div className="card" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1.6rem', marginTop: '0.8rem', padding: '0.6rem 0.8rem' }}>
                    <span>
                        {t('Ponuda', 'Offers')} <strong>{tot.count}</strong>
                    </span>
                    <span>
                        {t('Ponuđeno', 'Offered')} <strong>{money(tot.offer_eur)}</strong>
                    </span>
                    <span>
                        {t('Avansi', 'Advances')} <strong>{money(tot.advance_eur)}</strong>
                    </span>
                    <span>
                        {t('Finalne fakture', 'Final invoices')} <strong>{money(tot.final_invoice_eur)}</strong>
                    </span>
                    <span>
                        {t('Troškovi obrade', 'Processing')} <strong>{money(tot.processing_eur)}</strong>
                    </span>
                    <span>
                        {t('Razlike', 'Payments after analysis')} <strong>{money(tot.difference_eur)}</strong>
                    </span>
                    <span>
                        {t('Dug TCA', 'TCA balance')} <strong>{money(tot.dug_eur)}</strong>
                    </span>
                </div>
            ) : null}

            {error ? (
                <p role="alert" style={{ color: '#a00', marginTop: '0.8rem' }}>
                    {error}
                </p>
            ) : null}
            {loading && !data ? <p className="muted">{t('Učitavam...', 'Loading...')}</p> : null}

            {data ? (
                data.rows.length === 0 ? (
                    <p className="muted" style={{ marginTop: '1rem' }}>
                        {t('Nema ponuda za odabrani filter.', 'No offers for this filter.')}
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto', marginTop: '0.8rem' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                            <thead>
                                <tr>
                                    <th style={th}>{t('Ponuda', 'Offer')}</th>
                                    <th style={th}>{t('Datum', 'Date')}</th>
                                    <th style={thn}>{t('Ponuda EUR', 'Offer EUR')}</th>
                                    <th style={thn}>{t('Avans', 'Advance')}</th>
                                    <th style={th}>{t('Faktura AD', 'AD invoice')}</th>
                                    <th style={thn}>{t('Finalna', 'Final')}</th>
                                    <th style={thn}>{t('Obrada', 'Processing')}</th>
                                    <th style={thn}>{t('Razlika', 'Payment')}</th>
                                    <th style={thn}>{t('Dug', 'Balance')}</th>
                                    <th style={th}>{t('Faza', 'Stage')}</th>
                                    <th style={thn}>{t('Fine %', 'Fine %')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map((o) => (
                                    <tr key={o.id} onClick={() => setOpenId(o.id)} style={{ cursor: 'pointer' }}>
                                        <td style={td}>{o.offer_no}</td>
                                        <td style={td}>{o.offer_date ? formatDate(o.offer_date, locale) : ''}</td>
                                        <td style={num}>{money(o.offer_eur)}</td>
                                        <td style={num}>{money(o.advance_eur)}</td>
                                        <td style={td}>
                                            {o.ad_invoice_no ?? ''}
                                            {o.ad_invoice_no && !o.outgoing_invoice_id ? <span className="muted"> ({t('nije povezano', 'not linked')})</span> : null}
                                        </td>
                                        <td style={num}>{money(o.final_invoice_eur)}</td>
                                        <td style={num}>{money(o.processing_eur)}</td>
                                        <td style={num}>{money(o.difference_eur)}</td>
                                        <td style={{ ...num, fontWeight: Math.abs(o.dug_eur) >= 0.01 ? 600 : 400 }}>{money(o.dug_eur)}</td>
                                        <td style={td}>{stageLabel(o.stage)}</td>
                                        <td style={num}>{o.fine_dev_pct === null ? '' : formatNumber(Math.round(o.fine_dev_pct * 100) / 100, locale)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : null}

            {openId !== null ? (
                <OfferDialog
                    id={openId}
                    locale={locale}
                    token={token}
                    stageLabel={stageLabel}
                    onClose={(changed) => {
                        setOpenId(null);
                        if (changed) load();
                    }}
                />
            ) : null}
        </div>
    );
}

function OfferDialog({
    id,
    locale,
    token,
    stageLabel,
    onClose,
}: {
    id: number | 'new';
    locale: Locale;
    token: string | null;
    stageLabel: (s: Stage) => string;
    onClose: (changed: boolean) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const money = (v: number | string | null | undefined) => (v === null || v === undefined || v === '' ? '' : formatMoney(Number(v), locale));
    const [detail, setDetail] = useState<Detail | null>(null);
    const [form, setForm] = useState<Record<string, string>>({ partner_name: 'TCA' });
    const [links, setLinks] = useState<{ outgoing_invoice_id: number | null; ura_invoice_id: number | null; advance_bank_entry_id: number | null; difference_bank_entry_id: number | null }>({
        outgoing_invoice_id: null,
        ura_invoice_id: null,
        advance_bank_entry_id: null,
        difference_bank_entry_id: null,
    });
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [changed, setChanged] = useState(false);

    const fill = (d: Detail) => {
        setDetail(d);
        const f: Record<string, string> = {};
        FORM_FIELDS.forEach((x) => {
            const v = d.offer[x.key];
            f[x.key] = v === null || v === undefined ? '' : String(v);
        });
        f.note = d.offer.note ?? '';
        setForm(f);
        setLinks({
            outgoing_invoice_id: d.offer.outgoing_invoice_id,
            ura_invoice_id: d.offer.ura_invoice_id,
            advance_bank_entry_id: d.offer.advance_bank_entry_id,
            difference_bank_entry_id: d.offer.difference_bank_entry_id,
        });
    };

    const loadDetail = useCallback(
        (oid: number) =>
            api<Detail>(`/tca-invoices/${oid}`, token)
                .then(fill)
                .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.'))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [token],
    );

    useEffect(() => {
        if (id !== 'new') loadDetail(id);
    }, [id, loadDetail]);

    const save = async () => {
        setBusy(true);
        setError(null);
        const body: Record<string, unknown> = {};
        FORM_FIELDS.forEach((x) => {
            const v = form[x.key] ?? '';
            body[x.key] = x.kind === 'num' ? toNum(v) : v.trim() === '' ? null : v.trim();
        });
        if (!body.partner_name) body.partner_name = 'TCA';
        body.note = (form.note ?? '').trim() || null;
        Object.assign(body, links);
        try {
            if (id === 'new') {
                await mutate<{ id: number }>('/tca-invoices', token, 'POST', body);
                onClose(true);
                return;
            }
            await mutate(`/tca-invoices/${id}`, token, 'PATCH', body);
            setChanged(true);
            await loadDetail(id);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Spremanje nije uspjelo.', 'Saving failed.'));
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (id === 'new' || !window.confirm(t('Obrisati ponudu?', 'Delete offer?'))) return;
        try {
            await mutate(`/tca-invoices/${id}`, token, 'DELETE');
            onClose(true);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Brisanje nije uspjelo.', 'Delete failed.'));
        }
    };

    const o = detail?.offer;

    return (
        <div
            role="dialog"
            aria-modal="true"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', zIndex: 50, padding: '2rem 1rem' }}
        >
            <div className="card" style={{ background: '#fff', width: 'min(960px, 100%)', padding: '1rem 1.2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem' }}>
                    <h3 style={{ margin: 0 }}>{id === 'new' ? t('Nova ponuda TCA', 'New TCA offer') : `${t('Ponuda', 'Offer')} ${o?.offer_no ?? ''}`}</h3>
                    <button type="button" className="secondary compact" onClick={() => onClose(changed)}>
                        {t('Zatvori', 'Close')}
                    </button>
                </div>
                {o ? (
                    <p className="muted" style={{ margin: '0.3rem 0 0' }}>
                        {stageLabel(o.stage)} · {t('Dug', 'Balance')} {money(o.dug_eur)}
                        {o.fine_dev_pct !== null ? ` · ${t('Fine po analizi prema ponudi', 'Fine analysis vs offer')} ${formatNumber(Math.round(o.fine_dev_pct * 100) / 100, locale)} %` : ''}
                    </p>
                ) : null}
                {error ? (
                    <p role="alert" style={{ color: '#a00' }}>
                        {error}
                    </p>
                ) : null}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '0.5rem 0.8rem', marginTop: '0.8rem' }}>
                    {FORM_FIELDS.map((x) => (
                        <label key={x.key} style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
                            {t(x.hr, x.en)}
                            <input
                                type={x.kind === 'date' ? 'date' : 'text'}
                                inputMode={x.kind === 'num' ? 'decimal' : undefined}
                                value={form[x.key] ?? ''}
                                onChange={(e) => setForm((f) => ({ ...f, [x.key]: e.target.value }))}
                            />
                        </label>
                    ))}
                </div>
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                    {t('Napomena', 'Note')}
                    <textarea rows={2} value={form.note ?? ''} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
                </label>

                <h4 style={{ margin: '1rem 0 0.3rem' }}>{t('Veze', 'Links')}</h4>
                <LinkPicker
                    label={t('Izlazni račun (faktura AD)', 'Outgoing invoice (AD)')}
                    current={detail?.outgoing_invoice ? `${detail.outgoing_invoice.invoice_number}, ${formatDate(detail.outgoing_invoice.document_date, locale)}, ${money(detail.outgoing_invoice.amount_eur)}` : null}
                    linkedId={links.outgoing_invoice_id}
                    path={(q) => `/tca-invoices/lookup/outgoing?q=${encodeURIComponent(q)}`}
                    initialQuery={form.ad_invoice_no ?? ''}
                    render={(r: { id: number; invoice_number: string; document_date: string; customer_name: string | null; amount_eur: number | string }) =>
                        `${r.invoice_number} · ${formatDate(r.document_date, locale)} · ${r.customer_name ?? ''} · ${money(r.amount_eur)}`
                    }
                    onPick={(r) => {
                        setLinks((l) => ({ ...l, outgoing_invoice_id: r ? r.id : null }));
                        if (r) setForm((f) => ({ ...f, ad_invoice_no: r.invoice_number, final_invoice_eur: f.final_invoice_eur || String(r.amount_eur) }));
                    }}
                    locale={locale}
                    token={token}
                />
                <LinkPicker
                    label={t('Račun TCA za obradu (URA)', 'TCA processing invoice (URA)')}
                    current={detail?.ura_invoice ? `${detail.ura_invoice.invoice_number ?? detail.ura_invoice.document_number ?? ''}, ${detail.ura_invoice.supplier_name ?? ''}, ${money(detail.ura_invoice.amount_total)}` : null}
                    linkedId={links.ura_invoice_id}
                    path={(q) => `/tca-invoices/lookup/ura?q=${encodeURIComponent(q)}`}
                    initialQuery={form.processing_invoice_no ?? ''}
                    render={(r: { id: number; invoice_number: string | null; document_number: string | null; document_date: string | null; supplier_name: string | null; amount_total: number | string }) =>
                        `${r.invoice_number ?? ''} · ${r.document_number ?? ''} · ${r.supplier_name ?? ''} · ${money(r.amount_total)}`
                    }
                    onPick={(r) => {
                        setLinks((l) => ({ ...l, ura_invoice_id: r ? r.id : null }));
                        if (r) setForm((f) => ({ ...f, processing_invoice_no: r.invoice_number ?? f.processing_invoice_no, processing_eur: f.processing_eur || String(r.amount_total) }));
                    }}
                    locale={locale}
                    token={token}
                />
                {(['advance', 'difference'] as const).map((kind) => {
                    const col = kind === 'advance' ? 'advance_bank_entry_id' : 'difference_bank_entry_id';
                    const cur = detail?.bank[kind];
                    const amountKey = kind === 'advance' ? 'advance_eur' : 'difference_eur';
                    return (
                        <LinkPicker
                            key={kind}
                            label={kind === 'advance' ? t('Uplata avansa (izvod)', 'Advance payment (statement)') : t('Uplata razlike (izvod)', 'Post-analysis payment (statement)')}
                            current={cur ? `${formatDate(cur.statement_date, locale)}, ${cur.partner_name ?? ''}, ${money(cur.credit)}` : null}
                            linkedId={links[col]}
                            path={(q) => {
                                const amount = toNum(form[amountKey] ?? '');
                                const p = new URLSearchParams();
                                if (q.trim()) p.set('q', q.trim());
                                if (amount !== null) p.set('amount', String(amount));
                                return `/tca-invoices/lookup/bank?${p.toString()}`;
                            }}
                            initialQuery="TCA"
                            render={(r: BankRow) => `${formatDate(r.statement_date, locale)} · ${r.partner_name ?? ''} · ${(r.description ?? '').slice(0, 60)} · ${money(r.credit)}`}
                            onPick={(r) => setLinks((l) => ({ ...l, [col]: r ? r.id : null }))}
                            locale={locale}
                            token={token}
                        />
                    );
                })}

                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                    <button type="button" className="compact" disabled={busy || !(form.offer_no ?? '').trim()} onClick={save}>
                        {t('Spremi', 'Save')}
                    </button>
                    {id !== 'new' ? (
                        <button type="button" className="secondary compact" disabled={busy} onClick={remove}>
                            {t('Obriši', 'Delete')}
                        </button>
                    ) : null}
                </div>

                {detail ? (
                    <>
                        <h4 style={{ margin: '1.2rem 0 0.3rem' }}>{t('Tok ponude', 'Offer flow')}</h4>
                        <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <tbody>
                                {detail.timeline.map((s, i) => (
                                    <tr key={i}>
                                        <td style={{ padding: '0.2rem 0.6rem 0.2rem 0' }}>{s.date ? formatDate(s.date, locale) : '-'}</td>
                                        <td style={{ padding: '0.2rem 0.6rem' }}>{s.step}</td>
                                        <td style={{ padding: '0.2rem 0.6rem' }}>{s.ref ?? ''}</td>
                                        <td style={{ padding: '0.2rem 0.6rem', textAlign: 'right' }}>{money(s.amount_eur)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {detail.events.length > 0 ? (
                            <details style={{ marginTop: '0.8rem' }}>
                                <summary>
                                    {t('Dnevnik promjena', 'Change log')} ({detail.events.length})
                                </summary>
                                <ul style={{ fontSize: '0.8rem', margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
                                    {detail.events.map((e) => (
                                        <li key={e.id}>
                                            {e.created_at} · {e.user_name ?? t('uvoz', 'import')} · {e.action}
                                            {e.field ? ` ${e.field}: ${e.old_value ?? '-'} -> ${e.new_value ?? '-'}` : ''}
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        ) : null}
                    </>
                ) : null}
            </div>
        </div>
    );
}

function LinkPicker<R extends { id: number }>({
    label,
    current,
    linkedId,
    path,
    initialQuery,
    render,
    onPick,
    locale,
    token,
}: {
    label: string;
    current: string | null;
    linkedId: number | null;
    path: (q: string) => string;
    initialQuery: string;
    render: (r: R) => string;
    onPick: (r: R | null) => void;
    locale: Locale;
    token: string | null;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [q, setQ] = useState(initialQuery);
    const [rows, setRows] = useState<R[] | null>(null);
    const [picked, setPicked] = useState<string | null>(null);

    const search = async () => {
        try {
            const r = await api<{ rows: R[] }>(path(q), token);
            setRows(r.rows);
        } catch {
            setRows([]);
        }
    };

    return (
        <div style={{ borderTop: '1px solid #ddd', padding: '0.4rem 0', fontSize: '0.85rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ minWidth: '14rem' }}>{label}</strong>
                <span>{linkedId ? picked ?? current ?? `#${linkedId}` : <span className="muted">{t('nije povezano', 'not linked')}</span>}</span>
                {linkedId ? (
                    <button
                        type="button"
                        className="secondary compact"
                        onClick={() => {
                            setPicked(null);
                            onPick(null);
                        }}
                    >
                        {t('Ukloni vezu', 'Unlink')}
                    </button>
                ) : null}
                <input type="search" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: '10rem' }} aria-label={label} />
                <button type="button" className="secondary compact" onClick={search}>
                    {t('Traži', 'Search')}
                </button>
            </div>
            {rows !== null ? (
                rows.length === 0 ? (
                    <p className="muted" style={{ margin: '0.3rem 0 0' }}>
                        {t('Ništa nije pronađeno.', 'Nothing found.')}
                    </p>
                ) : (
                    <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem' }}>
                        {rows.map((r) => (
                            <li key={r.id}>
                                <button
                                    type="button"
                                    className="secondary compact"
                                    onClick={() => {
                                        setPicked(render(r));
                                        onPick(r);
                                        setRows(null);
                                    }}
                                >
                                    {t('Poveži', 'Link')}
                                </button>{' '}
                                {render(r)}
                            </li>
                        ))}
                    </ul>
                )
            ) : null}
        </div>
    );
}
